/**
 * GA Measurement IDs:
 * - Avant Prod: G-9CRZ94MKN3
 * - Avant UAT: G-1W5V6H8B68
 * - Avant QA: G-GETGLWTBXJ
 * - Avant Dev: G-KQ5GN1319Y
 * - Auxcap Prod: G-Y6NWCPZ1ZE
 * - Auxcap QA : G-720B815XRN
 * - Auxcap Dev: G-PB2RVH45X3
 * Override with REACT_APP_GA_MEASUREMENT_ID or REACT_APP_GA_MEASUREMENT_ID_AUXCAP.
 *
 * Hostname detection runs first because a single .env file (e.g. .env.production) may
 * define GA IDs for more than one tenant at once — deciding by the actual served domain
 * avoids one tenant's env var shadowing another's when both are present.
 */
const getGAMeasurementId = (): string => {
    // Priority 1: Hostname-based detection (ordered by specificity — most specific first)
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname.toLowerCase();

        // Avant environments (specific patterns first)
        if (hostname.includes('customer-avant-uat')) return 'G-1W5V6H8B68';
        if (hostname.includes('customer-avant-qa')) return 'G-GETGLWTBXJ';
        if (hostname.includes('myaccount-atfs')) return 'G-9CRZ94MKN3';

        // Auxcap environments (specific patterns first to avoid conflicts)
        if (hostname.includes('myaccount-auxcap')) return 'G-Y6NWCPZ1ZE';
        if (hostname.includes('customer-auxcap-qa')) return 'G-720B815XRN';
        if (hostname.includes('customer-dev2')) return 'G-KQ5GN1319Y';
        if (hostname.includes('customer-dev')) return 'G-PB2RVH45X3';
    }

    // Priority 2: Avant-specific GA measurement ID override (used when hostname is unrecognized, e.g. localhost/preview)
    if (process.env.REACT_APP_GA_MEASUREMENT_ID) {
        return process.env.REACT_APP_GA_MEASUREMENT_ID;
    }

    // Priority 3: Auxcap-specific GA measurement ID override
    if (process.env.REACT_APP_GA_MEASUREMENT_ID_AUXCAP) {
        return process.env.REACT_APP_GA_MEASUREMENT_ID_AUXCAP;
    }

    // Priority 4: Environment-based fallback
    const env = (process.env.REACT_APP_ENV || '').toLowerCase();
    const profileUrl = process.env.REACT_APP_PROFILE_URL || '';
    const isAuxcap = profileUrl.includes('auxcap');

    if (env === 'uat') return 'G-1W5V6H8B68';
    if (env === 'production' || env === 'prod') {
        return isAuxcap ? 'G-Y6NWCPZ1ZE' : 'G-9CRZ94MKN3';
    }
    if (env === 'qa') return 'G-720B815XRN';
    if (env === 'stage') return 'G-PB2RVH45X3';

    // Default: Auxcap Dev
    return 'G-PB2RVH45X3';
};

class AppConfig {
    ENV: string;
    apiDomain: string;
    pageAnimation: string;
    logLevel: string;
    appVersion: string;
    gaMeasurementId: string;
    stripePublishableKey: string;
    stripeSetupIntentClientSecret: string;

    constructor() {
        this.ENV = process.env.NODE_ENV ?? 'development';
        this.apiDomain = process.env.REACT_APP_API_URL ?? '';
        this.pageAnimation = 'slide';
        this.logLevel = 'info';
        this.appVersion = process.env.REACT_APP_APP_VERSION ?? '';
        this.gaMeasurementId = getGAMeasurementId();
        this.stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? '';
        this.stripeSetupIntentClientSecret = process.env.REACT_APP_STRIPE_SETUP_INTENT_CLIENT_SECRET ?? '';
    }
}

export const app_config = new AppConfig();
